import React, { useEffect, useState } from 'react';
import { Table, Button, Empty, Spin, Segmented } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { authApi } from '../../../api/auth.api.js';
import { toast } from '../../../utils/toast.js';
import { toTenScale } from '../../learning/utils/moduleGate.js';
import { StatusPill } from './AdminUi.jsx';

// Kết quả bài test module của một tài khoản, đặt trong modal "Chi tiết tài khoản" (chỉ ADMIN).
// API: GET /users/:userId/quiz-results. Xuất Excel 2 sheet:
//  - "Tong hop theo module": mỗi module 1 dòng (điểm cao nhất, lần gần nhất, số lần làm, đạt/chưa đạt)
//  - "Chi tiet tung lan lam": mọi lần làm bài, mới nhất trước

const fmtDate = (v) => (v ? new Date(v).toLocaleString('vi-VN', { hour12: false }) : '');
const fmtDuration = (sec) => {
  const s = Number(sec) || 0;
  return `${Math.floor(s / 60)} phút ${s % 60} giây`;
};
const toNumber10 = (score) => Math.round(Number(score) || 0) / 10;

function exportExcel(report) {
  const { user, summary, attempts } = report;
  const summaryRows = summary.map((s, i) => ({
    STT: i + 1,
    'Chủ đề': s.courseTitle,
    'Mã module': s.moduleCode,
    'Tên module': s.moduleTitle,
    'Số lần làm': s.attemptsCount,
    'Điểm cao nhất (/10)': toNumber10(s.bestScore),
    'Điểm lần gần nhất (/10)': toNumber10(s.lastScore),
    'Kết quả': s.passed ? 'Đạt' : 'Chưa đạt',
    'Ngày làm lần đầu': fmtDate(s.firstAttemptAt),
    'Ngày đạt lần đầu': fmtDate(s.firstPassedAt),
    'Ngày làm gần nhất': fmtDate(s.lastAttemptAt),
  }));
  const attemptRows = attempts.map((a, i) => ({
    STT: i + 1,
    'Thời gian nộp': fmtDate(a.createdAt),
    'Chủ đề': a.courseTitle,
    'Mã module': a.moduleCode,
    'Tên module': a.moduleTitle,
    'Lần làm thứ': a.attemptNumber,
    'Số câu đúng': `${a.correctCount}/${a.totalQuestions}`,
    'Điểm (/10)': toNumber10(a.score),
    'Kết quả': a.passed ? 'Đạt' : 'Chưa đạt',
    'Thời gian làm bài': fmtDuration(a.timeSpentSeconds),
  }));

  // 2 dòng đầu mỗi sheet ghi thông tin khách để file gửi đi vẫn biết là của ai
  const header = [
    [`Học viên: ${user.fullName || ''} - ${user.email || ''}${user.phone ? ` - ${user.phone}` : ''}`],
    [`Nhà thuốc: ${user.pharmacyName || '—'}${user.province ? ` (${user.province})` : ''} · Đạt khi trên 5/10 điểm · Xuất lúc ${fmtDate(new Date())}`],
    [],
  ];
  const makeSheet = (rows, widths) => {
    const ws = XLSX.utils.aoa_to_sheet(header);
    XLSX.utils.sheet_add_json(ws, rows, { origin: 'A4' });
    ws['!cols'] = widths.map((wch) => ({ wch }));
    return ws;
  };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, makeSheet(summaryRows, [5, 34, 10, 50, 10, 16, 18, 10, 20, 20, 20]), 'Tong hop theo module');
  XLSX.utils.book_append_sheet(wb, makeSheet(attemptRows, [5, 20, 34, 10, 50, 10, 12, 10, 10, 18]), 'Chi tiet tung lan lam');
  const namePart = (user.fullName || user.email || 'hoc-vien')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  XLSX.writeFile(wb, `ket-qua-test-${namePart}-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export function AdminUserQuizResults({ userId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('summary');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    authApi
      .getUserQuizResults(userId)
      .then((res) => alive && setReport(res.data))
      .catch((err) => alive && toast.error(err.message || 'Lỗi khi tải kết quả bài test'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [userId]);

  if (loading) {
    return (
      <div style={{ padding: '24px 0', textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }
  if (!report) return null;

  const { totals, summary, attempts } = report;
  const scoreCell = (v) => <strong>{toTenScale(v)}/10</strong>;
  const resultCell = (passed) => <StatusPill tone={passed ? 'good' : 'bad'}>{passed ? 'Đạt' : 'Chưa đạt'}</StatusPill>;
  const moduleCell = (_, r) => (
    <div>
      <strong>{r.moduleCode}</strong> <span className="adm-cell-sub">{r.courseTitle}</span>
    </div>
  );

  const summaryColumns = [
    { title: 'Module', key: 'module', render: moduleCell },
    { title: 'Số lần', dataIndex: 'attemptsCount', width: 70, align: 'center' },
    { title: 'Cao nhất', dataIndex: 'bestScore', width: 90, align: 'center', render: scoreCell },
    { title: 'Gần nhất', dataIndex: 'lastScore', width: 90, align: 'center', render: scoreCell },
    { title: 'Kết quả', dataIndex: 'passed', width: 100, render: resultCell },
    { title: 'Làm gần nhất', dataIndex: 'lastAttemptAt', width: 150, render: fmtDate },
  ];
  const attemptColumns = [
    { title: 'Thời gian nộp', dataIndex: 'createdAt', width: 150, render: fmtDate },
    { title: 'Module', key: 'module', render: moduleCell },
    { title: 'Lần', dataIndex: 'attemptNumber', width: 60, align: 'center' },
    { title: 'Đúng', key: 'correct', width: 70, align: 'center', render: (_, r) => `${r.correctCount}/${r.totalQuestions}` },
    { title: 'Điểm', dataIndex: 'score', width: 80, align: 'center', render: scoreCell },
    { title: 'Kết quả', dataIndex: 'passed', width: 100, render: resultCell },
  ];

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <div style={{ fontWeight: 700 }}>Kết quả bài test module</div>
          <div className="adm-cell-sub">
            Đã làm {totals.modulesTested} module · Đạt {totals.modulesPassed} · {totals.attempts} lượt làm bài · Đạt khi trên 5/10 điểm
          </div>
        </div>
        <Button icon={<DownloadOutlined />} disabled={!attempts.length} onClick={() => exportExcel(report)}>
          Xuất Excel
        </Button>
      </div>

      {!attempts.length ? (
        <Empty description="Tài khoản này chưa làm bài test nào" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <>
          <Segmented
            size="small"
            value={view}
            onChange={setView}
            options={[
              { label: 'Theo module', value: 'summary' },
              { label: 'Từng lần làm', value: 'attempts' },
            ]}
            style={{ marginBottom: 8 }}
          />
          <Table
            size="small"
            rowKey={view === 'summary' ? 'quizId' : 'id'}
            columns={view === 'summary' ? summaryColumns : attemptColumns}
            dataSource={view === 'summary' ? summary : attempts}
            pagination={{ pageSize: 8, hideOnSinglePage: true }}
            scroll={{ x: 640 }}
          />
        </>
      )}
    </div>
  );
}
